import { useEffect, useRef } from 'react';
import useGetParams from '../hooks/useGetParams';
import { useGenerateAiQueryMutation, useGetEventSellersReportQuery } from '../store';
import { formatCurrency } from '../utils/shared';
import LoadingLottie from './atoms/LoadingLottie';
import TitleNumberCard from './atoms/TitleNumberCard';
import CustomBarChart from './BarChart';
import AnimatedLoadingText from './atoms/AnimatedLoadingText';
import EventDTO from '../models/event';

const SellersReportTab = ({ event, hideAiSummary }: { event: EventDTO | null; hideAiSummary?: boolean }) => {
  const { eventId, businessId } = useGetParams();
  const { data, isLoading } = useGetEventSellersReportQuery({
    eventId,
    businessId,
  });

  const [generateAiQuery, { isLoading: isGeneratingAiQuery, data: aiQueryData }] = hideAiSummary
    ? [() => {}, { isLoading: false, data: null }]
    : useGenerateAiQueryMutation();
  const aiTriggeredForEventRef = useRef<string | null>(null);
  useEffect(() => {
    if (!data) {
      return;
    }
    if (aiTriggeredForEventRef.current === eventId) {
      return;
    }
    aiTriggeredForEventRef.current = eventId;
    const dataWithEventDate = {
      ...data,
      event_date: event?.date,
      event_end_date: event?.ends_at,
      current_date: new Date().toISOString(),
    };
    generateAiQuery({ contextJson: JSON.stringify(dataWithEventDate), businessId });
  }, [data, eventId, generateAiQuery, businessId]);

  if (isLoading) {
    return <LoadingLottie />;
  }

  if (!data) {
    return <div>No hay datos</div>;
  }

  const bestSellers = data?.best_sellers.map((seller) => ({
    name: seller.username || 'Sin nombre',
    pv: seller.total_amount,
  }));
  const averageSalesPerSeller = data?.order_average_best_sellers.map((seller) => ({
    name: seller.username || 'Sin nombre',
    pv: seller.average_order_amount,
  }));

  const bestReferrals = data?.best_referrals.map((referral) => ({
    name: referral.referral_code,
    pv: referral.total_amount,
  }));

  return (
    <div className='flex flex-col gap-8'>
      {!hideAiSummary && (
        <AnimatedLoadingText isLoading={isGeneratingAiQuery} loadingText="Generando reporte con IA..." text={aiQueryData?.data?.output_text} title='Resumen IA (BETA)'/>
      )}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 w-full glass-surface'>
        <TitleNumberCard
          title="Total vendido por referidos"
          number={formatCurrency(data?.total_referral_amount || 0)}
        />
        <TitleNumberCard
          title="Total vendido por vendedores"
          number={formatCurrency(data?.total_seller_amount || 0)}
        />
        <TitleNumberCard
          title="Comisiones totales vendedores y referidos"
          number={formatCurrency(data?.total_comission_amount || 0)}
        />
        <TitleNumberCard
          title="Venta neta vendedores"
          number={formatCurrency(data?.net_seller_amount || 0)}
        />
      </div>
      <div className='grid grid-cols-1 md:grid-cols-2 gap-8 w-full glass-surface'>
        <div className='w-full glass-surface'>
          <h1 className='text-center'>Vendedores con mejor rendimiento</h1>
          <CustomBarChart data={bestSellers || []} currency />
        </div>
        <div className='w-full glass-surface'>
          <h1 className='text-center'>Valor promedio de venta por vendedor</h1>
          <CustomBarChart data={averageSalesPerSeller || []} currency />
        </div>
        <div className='w-full glass-surface'>
          <h1 className='text-center'>Códigos de referidos con mejor rendimiento</h1>
          <CustomBarChart data={bestReferrals || []} currency />
        </div>
      </div>
    </div>
  );
};

export default SellersReportTab;
